import { useSyncExternalStore } from "react";
import { useLanguage, type Language } from "./i18n";
import type { Service } from "./types";

/**
 * The service catalogue, in the reader's language.
 *
 * WHY A LOOKUP RATHER THAN MORE COLUMNS ON EVERY QUERY: fifteen SQL statements
 * select `s.name AS service_name` — requests, the ledger, search, the gallery,
 * insights, saved briefs. Threading `name_ar` through all fifteen means fifteen
 * chances to forget one, and a forgotten one shows an English service name in the
 * middle of an Arabic table, which reads as a bug rather than as a gap. This
 * resolves by service id from the catalogue the app has already loaded, so there
 * is ONE place that decides what a service is called.
 *
 * A service name is only ever displayed beside a service id, so the id is always
 * available where the name is. Where it somehow is not, the English string the row
 * already carried is used — visibly untranslated, never blank.
 *
 * The store is module-level with `useSyncExternalStore`, exactly like `src/i18n` —
 * every screen that lists work needs this, and a context provider would touch more
 * files than the feature does.
 */

type Catalogue = {
  byId: Map<string, Service>;
  /** English category → Arabic. The English string stays the key: the request
   *  form's icons, thumbnails and ordering are all keyed off it. */
  categories: Record<string, string | null>;
};

const EMPTY: Catalogue = { byId: new Map(), categories: {} };

let catalogue: Catalogue = EMPTY;
const listeners = new Set<() => void>();

/**
 * Build the category map from the services themselves.
 *
 * Keeps the FIRST Arabic found for a category rather than the last value seen. A
 * plain `Object.fromEntries(services.map(...))` lets one untranslated service wipe
 * the Arabic for its whole category, because the last row wins — so adding a single
 * new service with no translation would silently turn every category heading back
 * to English. A test in service-text.test.ts holds this.
 */
export function categoryMap(services: Service[]): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  for (const service of services) {
    if (!map[service.category]) map[service.category] = service.categoryAr;
  }
  return map;
}

/** Called once whenever the catalogue is (re)loaded. */
export function setServiceCatalogue(services: Service[], categoriesAr?: Record<string, string | null>) {
  catalogue = {
    byId: new Map(services.map((service) => [service.id, service])),
    categories: categoriesAr ?? categoryMap(services),
  };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function snapshot() {
  return catalogue;
}

/** Test seam: drops the catalogue so one test cannot leak into the next. */
export function resetServiceCatalogue() {
  catalogue = EMPTY;
  for (const listener of listeners) listener();
}

/**
 * `fallback ?? ""` rather than `fallback || ""` at each site: an empty Arabic
 * string in the database means "not translated", and must fall through to the
 * English the same way a null does. Storing "" and rendering "" would print a
 * blank cell where a service name belongs.
 */
function pick(arabic: string | null | undefined, english: string | null | undefined, language: Language) {
  if (language === "ar" && arabic && arabic.trim()) return arabic;
  return english ?? "";
}

export type ServiceText = {
  /** The service's name. `fallback` is the English name the row already carried. */
  name: (serviceId: string | null | undefined, fallback?: string | null) => string;
  description: (serviceId: string | null | undefined, fallback?: string | null) => string;
  unit: (serviceId: string | null | undefined, fallback?: string | null) => string;
  /** Categories are identified by their English text, not an id. */
  category: (englishCategory: string | null | undefined) => string;
  /**
   * Everything a service can be searched by, in BOTH languages, lowercased.
   *
   * Always both: a client reading the Arabic catalogue may still type "reel", and
   * somebody on the English screen may paste an Arabic name from a chat. Matching
   * only the displayed language answers "nothing found" to a question that has an
   * answer — the same rule the ledger search follows (context.md trap 28).
   */
  haystack: (service: Service) => string;
};

export function serviceText(store: Catalogue, language: Language): ServiceText {
  const get = (serviceId: string | null | undefined) => (serviceId ? store.byId.get(serviceId) : undefined);
  return {
    name: (serviceId, fallback) => pick(get(serviceId)?.nameAr, get(serviceId)?.name ?? fallback, language),
    description: (serviceId, fallback) => pick(get(serviceId)?.descriptionAr, get(serviceId)?.description ?? fallback, language),
    unit: (serviceId, fallback) => pick(get(serviceId)?.unitAr, get(serviceId)?.unit ?? fallback, language),
    category: (englishCategory) => pick(englishCategory ? store.categories[englishCategory] : null, englishCategory, language),
    haystack: (service) => [
      service.name, service.category, service.description,
      service.nameAr, service.categoryAr, service.descriptionAr,
    ].filter(Boolean).join(" ").toLowerCase(),
  };
}

export function useServiceText(): ServiceText {
  const store = useSyncExternalStore(subscribe, snapshot, snapshot);
  const language = useLanguage();
  return serviceText(store, language);
}
